if (!customElements.get('cap-bundle-builder')) {
  customElements.define(
    'cap-bundle-builder',
    class CapBundleBuilder extends HTMLElement {
      connectedCallback() {
        this.products = this.parseJson('[data-bundle-products]');
        this.stickers = this.parseJson('[data-bundle-stickers]');
        this.tiers = [...this.querySelectorAll('[data-quantity]')];
        this.slots = this.querySelector('[data-bundle-slots]');
        this.gallery = this.querySelector('[data-bundle-gallery]');
        this.galleryCount = this.querySelector('[data-bundle-gallery-count]');
        this.progress = this.querySelector('[data-bundle-progress]');
        this.total = this.querySelector('[data-bundle-total]');
        this.compareTotal = this.querySelector('[data-bundle-compare-total]');
        this.saving = this.querySelector('[data-bundle-saving]');
        this.addButton = this.querySelector('[data-bundle-add]');
        this.addButtonLabel = this.addButton?.querySelector('span');
        this.defaultAddLabel = this.addButtonLabel?.textContent.trim() || 'Add bundle to cart';
        this.error = this.querySelector('[data-bundle-error]');
        this.empty = this.querySelector('[data-bundle-empty]');
        this.upgrade = this.querySelector('[data-bundle-upgrade]');
        this.completeLabel = this.querySelector('[data-bundle-complete]');
        this.celebration = this.querySelector('[data-bundle-celebration]');
        this.modeButtons = [...this.querySelectorAll('[data-bundle-mode]')];
        this.categoryButtons = [...this.querySelectorAll('[data-bundle-category]')];
        this.journeySteps = [...this.querySelectorAll('[data-journey-step]')];
        this.journeyLines = [...this.querySelectorAll('[data-journey-line]')];
        this.sticker = this.querySelector('[data-bundle-sticker]');
        this.stickerToggle = this.querySelector('[data-sticker-toggle]');
        this.stickerSelect = this.querySelector('[data-sticker-select]');
        this.stickerPrice = this.querySelector('[data-sticker-price]');
        this.selectedIds = [];
        this.activeCategory = 'all';
        this.wasComplete = false;

        this.tiers.forEach((button) => {
          const unavailable = Number(button.dataset.quantity) > this.products.length;
          button.disabled = unavailable;
          button.setAttribute('aria-disabled', String(unavailable));
          button.addEventListener('click', () => this.selectTier(button));
        });

        const preferredQuantity = Number(this.dataset.preferredTier || 3);
        const preferredTier =
          this.tiers.find(
            (tier) => Number(tier.dataset.quantity) === preferredQuantity && !tier.disabled
          ) || this.tiers.find((tier) => !tier.disabled);
        this.applyTier(preferredTier);

        this.modeButtons.forEach((button) => {
          button.addEventListener('click', () => this.quickStart(button.dataset.bundleMode));
        });
        this.categoryButtons.forEach((button) => {
          button.addEventListener('click', () => this.selectCategory(button.dataset.bundleCategory));
        });
        this.upgrade?.addEventListener('click', () => this.upgradeTier());
        this.addButton?.addEventListener('click', () => this.addBundle());

        this.setupStickerAddOn();
        this.empty?.classList.toggle('hidden', this.products.length > 0);
        this.renderGallery();
        this.updateInterface();
      }

      parseJson(selector) {
        try {
          return JSON.parse(this.querySelector(selector)?.textContent || '[]');
        } catch (error) {
          return [];
        }
      }

      applyTier(button) {
        if (!button) {
          this.quantity = 0;
          this.discount = 0;
          this.discountCode = '';
          return;
        }

        this.quantity = Number(button.dataset.quantity);
        this.discount = Number(button.dataset.discount || 0);
        this.discountCode = button.dataset.discountCode || '';
        this.selectedIds = this.selectedIds.slice(0, this.quantity);
        this.tiers.forEach((tier) => {
          const active = tier === button;
          tier.classList.toggle('is-active', active);
          tier.setAttribute('aria-checked', String(active));
        });
      }

      selectTier(button) {
        if (button.disabled) return;
        this.applyTier(button);
        this.setMode('choose');
        this.updateInterface();
        this.track('cap_bundle_tier_selected', {
          bundle_quantity: this.quantity,
          bundle_discount: this.discount
        });
      }

      quickStart(mode) {
        this.setMode(mode);
        if (mode === 'favourites') {
          this.selectedIds = this.products.slice(0, this.quantity).map((product) => String(product.id));
        } else if (mode === 'surprise') {
          this.selectedIds = [...this.products]
            .sort(() => Math.random() - 0.5)
            .slice(0, this.quantity)
            .map((product) => String(product.id));
        } else {
          this.selectedIds = [];
        }
        this.updateInterface();
        this.track('cap_bundle_quick_start', { mode, bundle_quantity: this.quantity });
      }

      setMode(mode) {
        this.modeButtons.forEach((button) => {
          const active = button.dataset.bundleMode === mode;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
      }

      selectCategory(category) {
        this.activeCategory = category || 'all';
        this.categoryButtons.forEach((button) => {
          const active = button.dataset.bundleCategory === this.activeCategory;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        this.renderGallery();
        this.syncGallery();
        this.track('cap_bundle_category_selected', { category: this.activeCategory });
      }

      filteredProducts() {
        if (!this.activeCategory || this.activeCategory === 'all') return this.products;
        return this.products.filter((product) => product.category === this.activeCategory);
      }

      renderGallery() {
        if (!this.gallery) return;
        const products = this.filteredProducts();
        this.gallery.innerHTML = products
          .map(
            (product) => `
              <button
                type="button"
                class="cap-bundle__product"
                data-bundle-product="${product.id}"
                aria-pressed="false"
              >
                <span class="cap-bundle__product-media">
                  ${
                    product.image
                      ? `<img src="${product.image}" alt="" width="180" height="180" loading="lazy">`
                      : '<span class="cap-bundle__product-placeholder"></span>'
                  }
                  <i data-selection-number aria-hidden="true"></i>
                </span>
                <strong>${this.escapeHtml(product.title)}</strong>
                ${
                  product.variantTitle
                    ? `<small>${this.escapeHtml(product.variantTitle)}</small>`
                    : ''
                }
                ${
                  product.categoryLabel
                    ? `<small class="cap-bundle__product-type">${this.escapeHtml(product.categoryLabel)}</small>`
                    : ''
                }
                <span>${this.formatMoney(product.price)}</span>
              </button>
            `
          )
          .join('') || '<p class="cap-bundle__category-empty">No in-stock products in this type right now.</p>';

        this.gallery.querySelectorAll('[data-bundle-product]').forEach((button) => {
          button.addEventListener('click', () => this.toggleProduct(button.dataset.bundleProduct));
        });
        if (this.galleryCount) {
          this.galleryCount.textContent =
            this.activeCategory === 'all'
              ? `${this.products.length} in-stock options`
              : `${products.length} in-stock ${this.activeCategory} option${products.length === 1 ? '' : 's'}`;
        }
      }

      toggleProduct(id) {
        const existingIndex = this.selectedIds.indexOf(String(id));
        if (existingIndex >= 0) {
          this.selectedIds.splice(existingIndex, 1);
        } else if (this.selectedIds.length < this.quantity) {
          this.selectedIds.push(String(id));
        } else {
          this.error.textContent = 'Your bundle is full. Remove an item or unlock the next bundle size.';
          this.slots?.classList.remove('is-nudging');
          requestAnimationFrame(() => this.slots?.classList.add('is-nudging'));
          return;
        }

        this.error.textContent = '';
        this.setMode('choose');
        this.updateInterface();
        this.track('cap_bundle_cap_toggled', {
          variant_id: Number(id),
          selected_count: this.selectedIds.length,
          bundle_quantity: this.quantity
        });
      }

      removeProduct(index) {
        this.selectedIds.splice(index, 1);
        this.setMode('choose');
        this.updateInterface();
      }

      renderSlots() {
        if (!this.slots) return;
        this.slots.innerHTML = Array.from({ length: this.quantity }, (_, index) => {
          const product = this.productById(this.selectedIds[index]);
          if (!product) {
            return `
              <button type="button" class="cap-bundle__slot is-empty" data-empty-slot>
                <span class="cap-bundle__slot-number">${index + 1}</span>
                <span class="cap-bundle__slot-plus">+</span>
                <strong>Choose item ${index + 1}</strong>
              </button>
            `;
          }

          return `
            <article class="cap-bundle__slot is-filled">
              <span class="cap-bundle__slot-number">${index + 1}</span>
              ${
                product.image
                  ? `<img class="cap-bundle__slot-image" src="${product.image}" alt="" width="112" height="112">`
                  : ''
              }
              <div class="cap-bundle__slot-copy">
                <strong>${this.escapeHtml(product.title)}</strong>
                <span>${this.escapeHtml(product.categoryLabel || 'Bundle item')} · ${this.formatMoney(product.price)}</span>
              </div>
              <button type="button" class="cap-bundle__slot-remove" data-remove-index="${index}" aria-label="Remove ${this.escapeHtml(
                product.title
              )}">×</button>
            </article>
          `;
        }).join('');

        this.slots.querySelectorAll('[data-remove-index]').forEach((button) => {
          button.addEventListener('click', () => this.removeProduct(Number(button.dataset.removeIndex)));
        });
        this.slots.querySelectorAll('[data-empty-slot]').forEach((button) => {
          button.addEventListener('click', () => {
            this.gallery?.scrollIntoView({ behavior: this.reducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
            this.gallery?.querySelector('button:not(.is-selected)')?.focus({ preventScroll: true });
          });
        });
      }

      syncGallery() {
        this.gallery?.querySelectorAll('[data-bundle-product]').forEach((button) => {
          const selectedIndex = this.selectedIds.indexOf(String(button.dataset.bundleProduct));
          const selected = selectedIndex >= 0;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', String(selected));
          button.querySelector('[data-selection-number]').textContent = selected ? selectedIndex + 1 : '';
        });
      }

      updateInterface() {
        this.renderSlots();
        this.syncGallery();

        const selected = this.selectedProducts();
        const remaining = Math.max(this.quantity - selected.length, 0);
        const complete = this.quantity > 0 && remaining === 0;
        const hasCollectorCase = this.hasCollectorCase(selected);
        const capsTotal = selected.reduce((sum, product) => sum + Number(product.price || 0), 0);
        const bundleDiscountApplies = complete && !hasCollectorCase;
        const capsDiscount = bundleDiscountApplies ? Math.round(capsTotal * (this.discount / 100)) : 0;
        const sticker = this.selectedSticker();
        const compareTotal = capsTotal + Number(sticker?.price || 0);
        const total = compareTotal - capsDiscount;

        if (this.progress) {
          if (complete) {
            if (hasCollectorCase) {
              this.progress.textContent = 'Bundle complete — collector case offer kept separate';
            } else {
              this.progress.textContent =
                capsDiscount > 0
                  ? `Bundle complete — you save ${this.formatMoney(capsDiscount)}`
                  : 'Bundle complete — ready to add';
            }
          } else if (selected.length === 0) {
            this.progress.textContent = `Pick your first item — ${this.quantity} needed`;
          } else if (remaining === 1) {
            this.progress.textContent = 'Great start — one more completes your bundle';
          } else {
            this.progress.textContent = `${selected.length} picked — choose ${remaining} more`;
          }
        }

        this.total.textContent = this.formatMoney(total);
        const showSaving = capsDiscount > 0 && complete;
        this.compareTotal?.classList.toggle('hidden', !showSaving);
        this.saving?.classList.toggle('hidden', !showSaving);
        if (this.compareTotal) this.compareTotal.textContent = showSaving ? this.formatMoney(compareTotal) : '';
        if (this.saving) {
          this.saving.textContent = showSaving
            ? `You save ${this.discount}% (${this.formatMoney(capsDiscount)})`
            : '';
        }

        this.completeLabel?.classList.toggle('hidden', !complete);
        this.addButton.disabled = !complete || this.products.length === 0;
        if (this.addButtonLabel) {
          this.addButtonLabel.textContent = complete
            ? `ADD MY BUNDLE · ${this.formatMoney(total)}`
            : this.defaultAddLabel;
        }
        this.addButton.classList.toggle('is-ready', complete);
        this.sticker?.classList.toggle('hidden', !complete || this.stickers.length === 0);

        this.updateJourney(selected.length, complete);
        this.updateUpgrade(complete);

        if (complete && !this.wasComplete) this.celebrate();
        this.wasComplete = complete;
      }

      updateJourney(selectedCount, complete) {
        const hasStarted = selectedCount > 0;
        this.journeySteps.forEach((step) => {
          const name = step.dataset.journeyStep;
          const active =
            name === 'pick' ? !hasStarted : name === 'save' ? hasStarted && !complete : complete;
          const done = name === 'pick' ? hasStarted : name === 'save' ? complete : false;
          step.classList.toggle('is-active', active);
          step.classList.toggle('is-complete', done);
        });
        this.journeyLines.forEach((line, index) => {
          line.style.width = `${index === 0 ? (hasStarted ? 100 : 0) : complete ? 100 : 0}%`;
        });
      }

      updateUpgrade(complete) {
        if (!this.upgrade) return;
        const nextTier = this.nextTier();
        const shouldShow = complete && nextTier && Number(nextTier.dataset.discount) > this.discount;
        this.upgrade.classList.toggle('hidden', !shouldShow);
        this.upgrade.dataset.targetQuantity = shouldShow ? nextTier.dataset.quantity : '';
        if (shouldShow) {
          const extra = Number(nextTier.dataset.quantity) - this.quantity;
          this.upgrade.innerHTML = `<strong>Add ${extra} more item${
            extra === 1 ? '' : 's'
          } to unlock ${nextTier.dataset.discount}% off</strong><span>Upgrade bundle →</span>`;
        }
      }

      nextTier() {
        return [...this.tiers]
          .filter((tier) => !tier.disabled && Number(tier.dataset.quantity) > this.quantity)
          .sort((a, b) => Number(a.dataset.quantity) - Number(b.dataset.quantity))[0];
      }

      upgradeTier() {
        const target = Number(this.upgrade?.dataset.targetQuantity);
        const tier = this.tiers.find((item) => Number(item.dataset.quantity) === target);
        if (!tier) return;
        this.applyTier(tier);
        this.updateInterface();
        requestAnimationFrame(() => {
          this.slots?.lastElementChild?.classList.add('is-new');
        });
        this.track('cap_bundle_tier_unlocked', {
          bundle_quantity: this.quantity,
          bundle_discount: this.discount
        });
      }

      setupStickerAddOn() {
        if (!this.sticker || this.stickers.length === 0) return;
        this.stickerSelect.innerHTML = this.stickers
          .map(
            (product) =>
              `<option value="${product.id}">${this.escapeHtml(product.title)}${
                product.variantTitle ? ` - ${this.escapeHtml(product.variantTitle)}` : ''
              }</option>`
          )
          .join('');
        this.stickerToggle.addEventListener('change', () => {
          this.stickerSelect.disabled = !this.stickerToggle.checked;
          this.stickerPrice.textContent = this.stickerToggle.checked
            ? `+ ${this.formatMoney(this.selectedSticker()?.price || 0)}`
            : '';
          this.updateInterface();
        });
        this.stickerSelect.addEventListener('change', () => {
          this.stickerPrice.textContent = `+ ${this.formatMoney(this.selectedSticker()?.price || 0)}`;
          this.updateInterface();
        });
      }

      selectedSticker() {
        if (!this.stickerToggle?.checked) return null;
        return this.stickers.find((item) => String(item.id) === this.stickerSelect.value) || null;
      }

      selectedProducts() {
        return this.selectedIds.map((id) => this.productById(id)).filter(Boolean);
      }

      productById(id) {
        return this.products.find((product) => String(product.id) === String(id));
      }

      hasCollectorCase(products = this.selectedProducts()) {
        return products.some((product) => {
          const title = `${product.title || ''} ${product.variantTitle || ''}`.toLowerCase();
          return product.category === 'case' || title.includes('collector case') || title.includes('case only');
        });
      }

      celebrate() {
        if (!this.celebration || this.reducedMotion()) return;
        this.celebration.replaceChildren();
        for (let index = 0; index < 10; index += 1) {
          const particle = document.createElement('i');
          particle.style.setProperty('--x', `${(Math.random() - 0.5) * 180}px`);
          particle.style.setProperty('--y', `${-30 - Math.random() * 110}px`);
          particle.style.setProperty('--delay', `${Math.random() * 120}ms`);
          this.celebration.appendChild(particle);
        }
        this.celebration.classList.remove('is-celebrating');
        requestAnimationFrame(() => this.celebration.classList.add('is-celebrating'));
        window.setTimeout(() => this.celebration.classList.remove('is-celebrating'), 1000);
      }

      async addBundle() {
        const products = this.selectedProducts();
        if (products.length !== this.quantity) return;
        const ids = products.map((product) => Number(product.id));
        const sticker = this.selectedSticker();
        const hasCollectorCase = this.hasCollectorCase(products);
        const activeDiscountCode = hasCollectorCase ? '' : this.discountCode;
        const cartItems = ids.map((id) => ({
          id,
          quantity: 1,
          properties: {
            _bundle_tier: `${this.quantity}-pack`,
            _bundle_discount: activeDiscountCode ? `${this.discount}%` : 'Collector case offer separate'
          }
        }));
        if (sticker) cartItems.push({ id: Number(sticker.id), quantity: 1 });

        this.setLoading(true);
        this.error.textContent = '';
        try {
          const cartDrawer = document.querySelector('cart-drawer');
          const sectionsToRender = cartDrawer?.getSectionsToRender?.() || [
            { id: 'cart-drawer' },
            { id: 'cart-icon-bubble' }
          ];
          const sectionIds = sectionsToRender.map((section) => section.id);
          const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: cartItems,
              sections: sectionIds,
              sections_url: window.location.pathname
            })
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || 'Unable to add this bundle.');

          this.track('cap_bundle_added', {
            bundle_quantity: this.quantity,
            bundle_discount: this.discount,
            variant_ids: ids,
            sticker_variant_id: sticker?.id || null
          });

          let discountResult;
          try {
            discountResult = await this.applyDiscountCode(sectionIds, activeDiscountCode, hasCollectorCase);
          } catch (error) {
            discountResult = {
              applied: false,
              message: activeDiscountCode
                ? `Bundle added, but discount ${activeDiscountCode} could not be verified. Please check the cart before checkout.`
                : '',
              sections: null
            };
          }
          await this.refreshCartDrawer(
            result,
            cartDrawer,
            sectionIds,
            discountResult.sections
          );
          this.showCartSurprise(cartDrawer, products);
          if (!discountResult.applied) {
            this.track('cap_bundle_discount_unverified', {
              bundle_quantity: this.quantity,
              discount_code: activeDiscountCode
            });
          }
        } catch (error) {
          this.error.textContent = error.message;
        } finally {
          this.setLoading(false);
          this.updateInterface();
        }
      }

      showCartSurprise(cartDrawer, products) {
        const drawer = cartDrawer?.querySelector('#CartDrawer');
        const drawerBody = drawer?.querySelector('.cart-drawer__body');
        if (!drawer || !drawerBody || !products.length) return;

        drawer.querySelector('[data-cart-surprise]')?.remove();
        const taggedProducts = products.filter((product) => product.surprisePick);
        const candidates = taggedProducts.length ? taggedProducts : products;
        const product = candidates[Math.floor(Math.random() * candidates.length)];
        const messages = [
          'That one keeps turning heads.',
          'Clean choice. The finishing touch for your cockpit.',
          'Your cockpit just levelled up.',
          'A small detail with serious character.'
        ];
        const message = taggedProducts.length
          ? this.dataset.surpriseMessage
          : this.dataset.surpriseFallback;
        const caption = messages[Math.floor(Math.random() * messages.length)];
        const animation = ['fade', 'lift', 'soft-pop'][Math.floor(Math.random() * 3)];
        const surprise = document.createElement('aside');
        surprise.className = `cart-cap-surprise cart-cap-surprise--${animation}`;
        surprise.dataset.cartSurprise = '';
        surprise.setAttribute('role', 'status');
        surprise.innerHTML = `
          <button class="cart-cap-surprise__close" type="button" aria-label="Dismiss surprise">&times;</button>
          <div class="cart-cap-surprise__stage" aria-hidden="true">
            <span class="cart-cap-surprise__cap">
              ${
                product.image
                  ? `<img src="${this.escapeHtml(product.image)}" alt="" width="92" height="92">`
                  : '<i></i>'
              }
            </span>
            <i class="cart-cap-surprise__spark"></i>
          </div>
          <div class="cart-cap-surprise__copy">
            <small>Nice pick</small>
            <strong>${this.escapeHtml(message || 'You picked a rider favourite')}</strong>
            <span>${this.escapeHtml(product.title)}</span>
            <p>${this.escapeHtml(caption)}</p>
          </div>
        `;
        surprise
          .querySelector('.cart-cap-surprise__close')
          ?.addEventListener('click', () => surprise.remove());
        drawerBody.prepend(surprise);
        requestAnimationFrame(() => surprise.classList.add('is-revealing'));
        window.setTimeout(() => surprise.classList.add('is-settled'), 4200);
        this.track('cap_cart_surprise_shown', {
          variant_id: product.id,
          tagged_daily_pick: taggedProducts.length > 0,
          animation
        });
      }

      async applyDiscountCode(sectionIds, discountCode = this.discountCode, clearExisting = false) {
        if (!discountCode && !clearExisting) return { applied: true, message: '', sections: null };

        const response = await fetch(`${this.dataset.cartUrl}/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
          body: JSON.stringify({
            discount: discountCode,
            sections: sectionIds,
            sections_url: window.location.pathname
          })
        });
        const cart = await response.json();
        if (!response.ok) {
          return {
            applied: false,
            message: discountCode
              ? `Bundle added, but discount ${discountCode} could not be applied. Please check the cart before checkout.`
              : '',
            sections: cart.sections || null
          };
        }

        if (!discountCode && clearExisting) {
          return { applied: true, message: '', sections: cart.sections || null };
        }

        const expectedCode = this.normalizeDiscountTitle(discountCode);
        const discountTitles = [
          ...(cart.cart_level_discount_applications || []).map((discount) => discount.title),
          ...(cart.items || []).flatMap((item) =>
            (item.discounts || []).map((discount) => discount.title)
          )
        ]
          .filter(Boolean)
          .map((title) => this.normalizeDiscountTitle(title));
        const applied = discountTitles.some(
          (title) =>
            title === expectedCode ||
            (title.length >= 5 &&
              expectedCode.length >= 5 &&
              (title.includes(expectedCode) || expectedCode.includes(title)))
        );

        return {
          applied,
          message: applied
            ? ''
            : `Bundle added, but ${discountCode} has not appeared in the cart yet. Please confirm the saving before checkout.`,
          sections: cart.sections || null
        };
      }

      validCartDrawerSections(sections) {
        const drawerHtml = sections?.['cart-drawer'];
        if (typeof drawerHtml !== 'string' || drawerHtml.trim() === '') return false;

        const parsedDrawer = new DOMParser()
          .parseFromString(drawerHtml, 'text/html')
          .querySelector('#CartDrawer');
        return Boolean(parsedDrawer?.querySelector('.drawer__inner'));
      }

      validSectionHtml(html, selector = '.shopify-section') {
        if (typeof html !== 'string' || html.trim() === '') return false;
        return Boolean(
          new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
        );
      }

      renderCartDrawerSection(cartDrawer, sections) {
        const parsedDrawer = new DOMParser()
          .parseFromString(sections['cart-drawer'], 'text/html')
          .querySelector('#CartDrawer');
        const currentDrawer = cartDrawer.querySelector('#CartDrawer');
        if (!parsedDrawer || !currentDrawer) return false;

        cartDrawer.close?.();
        cartDrawer.classList.remove('active', 'animate');
        currentDrawer.innerHTML = parsedDrawer.innerHTML;
        cartDrawer.classList.remove('is-empty');

        const iconHtml = sections['cart-icon-bubble'];
        const currentIcon = document.getElementById('cart-icon-bubble');
        if (currentIcon && typeof iconHtml === 'string') {
          const parsedIcon = new DOMParser()
            .parseFromString(iconHtml, 'text/html')
            .querySelector('#cart-icon-bubble');
          if (parsedIcon) currentIcon.innerHTML = parsedIcon.innerHTML;
        }

        const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
        overlay?.addEventListener('click', () => cartDrawer.close?.());
        try {
          cartDrawer.open?.(this.addButton);
        } catch (error) {
          cartDrawer.close?.();
          return false;
        }
        return true;
      }

      async cartDrawerOpenedCorrectly(cartDrawer) {
        await this.wait(350);
        const inner = cartDrawer.querySelector('.drawer__inner');
        const heading = inner?.querySelector('.drawer__header');
        const body = inner?.querySelector('.cart-drawer__body');
        const rect = inner?.getBoundingClientRect();
        return Boolean(
          cartDrawer.classList.contains('active') &&
          inner &&
          heading &&
          body &&
          rect &&
          rect.width > 100 &&
          rect.right > 0 &&
          rect.left < window.innerWidth &&
          inner.textContent.trim().length > 20
        );
      }

      async refreshCartDrawer(
        addResult,
        existingCartDrawer,
        existingSectionIds,
        discountedSections
      ) {
        if (window.customElements?.whenDefined) {
          await Promise.race([
            window.customElements.whenDefined('cart-drawer'),
            this.wait(1500)
          ]);
        }

        const cartDrawer = existingCartDrawer || document.querySelector('cart-drawer');
        if (!cartDrawer) {
          throw new Error('Your bundle was added, but the cart drawer is unavailable. Please use the cart icon.');
        }

        cartDrawer.setActiveElement?.(this.addButton);
        const sectionIds = existingSectionIds?.length
          ? existingSectionIds
          : ['cart-drawer', 'cart-icon-bubble'];
        const sectionsToRender = cartDrawer.getSectionsToRender?.() || sectionIds.map((id) => ({ id }));
        const sections = { ...(addResult.sections || {}) };

        Object.entries(discountedSections || {}).forEach(([id, html]) => {
          const section = sectionsToRender.find((item) => item.id === id);
          const selector = section?.selector || '.shopify-section';
          if (this.validSectionHtml(html, selector)) sections[id] = html;
        });

        if (!this.validCartDrawerSections(sections)) {
          cartDrawer.close?.();
          throw new Error('Your bundle was added, but the cart drawer could not be loaded. Please use the cart icon.');
        }

        const missingSection = sectionsToRender.find(
          (section) => !this.validSectionHtml(
            sections[section.id],
            section.selector || '.shopify-section'
          )
        );
        if (missingSection) {
          cartDrawer.close?.();
          throw new Error('Your bundle was added, but the cart could not finish loading. Please use the cart icon.');
        }

        if (!this.renderCartDrawerSection(cartDrawer, sections)) {
          cartDrawer.close?.();
          throw new Error('Your bundle was added, but the cart drawer could not be updated.');
        }

        if (!(await this.cartDrawerOpenedCorrectly(cartDrawer))) {
          cartDrawer.close?.();
          await this.wait(100);
          if (
            !this.renderCartDrawerSection(cartDrawer, sections) ||
            !(await this.cartDrawerOpenedCorrectly(cartDrawer))
          ) {
            cartDrawer.close?.();
            throw new Error('Your bundle was added, but the cart drawer could not open. Please use the cart icon.');
          }
        }
      }

      setLoading(loading) {
        this.addButton.disabled = loading;
        this.addButtonLabel?.classList.toggle('hidden', loading);
        this.addButton
          .querySelector('.loading-overlay__spinner')
          ?.classList.toggle('hidden', !loading);
      }

      track(event, data = {}) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event, ...data });
      }

      reducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      normalizeDiscountTitle(value) {
        return String(value || '')
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, '');
      }

      wait(milliseconds) {
        return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
      }

      formatMoney(cents) {
        if (window.Shopify?.formatMoney) {
          return window.Shopify.formatMoney(cents, this.dataset.moneyFormat);
        }
        return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'AUD'
        }).format(cents / 100);
      }

      escapeHtml(value) {
        const element = document.createElement('div');
        element.textContent = value || '';
        return element.innerHTML;
      }
    }
  );
}
